import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  ArrowLeft,
  ArrowRight,
  Spinner,
  Package,
  Boat,
  Airplane,
  Truck,
  CurrencyDollar,
  Calendar,
  PaperPlaneTilt,
  FloppyDisk,
  Warning,
  CheckCircle,
  Plus,
  X,
  User,
  Sparkle,
  WarningCircle,
} from '@phosphor-icons/react'
import { api } from '../../services/api'
import { FeeBreakdown, BreakdownItem } from '../../components/offer/FeeBreakdown'
import { MultiCurrencyTotal } from '../../components/offer/MultiCurrencyTotal'
import { SavedTemplatesPanel } from '../../components/offer/SavedTemplatesPanel'
import { OfferConditionsPanel } from '../../components/offer/OfferConditionsPanel'
import { ErrorModal } from '../../components/ErrorModal'
import { useOfferConditions } from '../../hooks/useOfferConditions'
import { isExpired, formatDateBR } from '../../lib/date'
import { useExchangeRate, formatCurrency } from '../../hooks/useExchangeRate'
import { computeTotalsByCurrency } from '../../utils/offerTotals'
import { computeCargoMetrics } from '../../utils/cargoMetrics'
import { FEE_UNIT_MAP, computeFeeSubtotal } from '../../constants/feeUnits'
import { DocumentAttachments } from '../../components/DocumentAttachments'
import { uploadOfferDocument, type AttachedDocument } from '../../services/quoteDocuments'
import { Dialog, DialogHeader, DialogContent, DialogFooter } from '../../components/ui/Dialog'

// Supported currencies for offers
export const SUPPORTED_CURRENCIES = [
  { code: 'BRL', symbol: 'R$', name: 'Real Brasileiro' },
  { code: 'USD', symbol: 'US$', name: 'Dólar Americano' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'Libra Esterlina' },
  { code: 'CNY', symbol: '¥', name: 'Yuan Chinês' },
] as const

export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number]['code']

// Map a currency symbol or code from the AI extraction to a supported ISO code.
// Returns null when it can't be recognized (so we don't wrongly coerce to USD).
function normalizeCurrency(raw: any): SupportedCurrency | null {
  const s = String(raw ?? '').trim().toUpperCase()
  if (!s) return null
  if (s.includes('R$') || s.includes('BRL') || s.includes('REAL')) return 'BRL'
  if (s.includes('US$') || s.includes('USD') || s.includes('DÓLAR') || s.includes('DOLAR') || s === '$') return 'USD'
  if (s.includes('€') || s.includes('EUR')) return 'EUR'
  if (s.includes('£') || s.includes('GBP')) return 'GBP'
  if (s.includes('¥') || s.includes('CNY') || s.includes('RMB')) return 'CNY'
  const codes = SUPPORTED_CURRENCIES.map(c => c.code) as string[]
  return codes.includes(s) ? (s as SupportedCurrency) : null
}

// Validation schema
const offerSchema = z.object({
  deliveryTime: z.string().optional(),
  validityDate: z.string().optional(),
  paymentTerms: z.string().optional(),
  paymentTermDays: z.string().optional(),
  spread: z.string().optional(),
  notes: z.string().optional(),
})

type OfferFormData = z.infer<typeof offerSchema>

interface OpportunityDetails {
  id: string
  txControl: string
  status: string
  incoterm: string
  origin: any
  destination: any
  pickupLocation: any
  goodsDescription: string
  goodsSpecial: string[]
  goodsType: string
  volumeItems: any[]
  documents: any[]
  notes?: string | null
  createdAt: string
  modalityId: string
  modalityName: string
  modalityType: string
  volumeType: string
  shipperName: string
  shipperTradingName: string
  totalOffersCount: number
  quoteDeadline: string | null
}

interface MyExistingOffer {
  id: string
  status: string
  totalValue: number
  currency: string
  createdAt: string
}

export default function NewOfferPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Detect if we're editing an existing offer vs creating a new one
  const isEditMode = location.pathname.includes('/offers/') && !location.pathname.includes('/opportunities/')

  // Form handling
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<OfferFormData>({
    resolver: zodResolver(offerSchema),
  })

  // Ref to prevent double submission
  const isSubmittingRef = useRef(false)

  // State
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [opportunity, setOpportunity] = useState<OpportunityDetails | null>(null)
  const [currentOfferId, setCurrentOfferId] = useState<string | null>(null)
  const [, setMyOffer] = useState<MyExistingOffer | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [readOnly, setReadOnly] = useState(false)
  const [readOnlyStatus, setReadOnlyStatus] = useState<string>('')
  const [editingSent, setEditingSent] = useState(false)
  const [offerHistory, setOfferHistory] = useState<Array<{ id: string; changedByName: string | null; snapshot: any; createdAt: string }>>([])
  const [platformFees, setPlatformFees] = useState<{ feeDesconsolidacao: number; feeLogistic: number } | null>(null)
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [errorDetails, setErrorDetails] = useState<{
    message: string
    details?: string
    code?: string
    endpoint?: string
  } | null>(null)
  const [breakdown, setBreakdown] = useState<BreakdownItem[]>([])
  const [freeTimeDays, setFreeTimeDays] = useState<string>('')
  const [carrierName, setCarrierName] = useState<string>('')
  const [routeType, setRouteType] = useState<'direct' | 'transshipment'>('direct')
  const [routeLegs, setRouteLegs] = useState<Array<{ port: string; country: string; eta?: string; etd?: string }>>([])
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; name: string; email: string; department?: string | null; role: string }>>([])
  const [responsibleUserIds, setResponsibleUserIds] = useState<string[]>([])
  const [selectedConditionIds, setSelectedConditionIds] = useState<string[]>([])
  const [offerDocs, setOfferDocs] = useState<AttachedDocument[]>([])
  const [extracting, setExtracting] = useState(false)
  const [reviewData, setReviewData] = useState<{ extracted: any } | null>(null)
  const extractInputRef = useRef<HTMLInputElement>(null)
  // Store original conditions from offer when editing (to avoid localStorage mismatch)
  const [originalOfferConditions, setOriginalOfferConditions] = useState<Array<{id: string, title: string, content: string}> | null>(null)

  // Exchange rate hook — PTAX is the canonical rate for offer persistence.
  // We snapshot rate + source into the offer record (exchange_rate_used,
  // exchange_rate_source, valueBRL, valueUSD) so the conversion never drifts
  // after creation.
  const { preferredCurrency } = useExchangeRate({
    source: 'ptax',
  })

  // Selected currency for this offer (starts with user preference, can be changed)
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(preferredCurrency as SupportedCurrency)

  // Update selected currency when preference loads (only if not already changed by user)
  useEffect(() => {
    if (preferredCurrency && !isEditMode) {
      setSelectedCurrency(preferredCurrency as SupportedCurrency)
    }
  }, [preferredCurrency, isEditMode])

  // Input currency is now the selected currency
  const inputCurrency = selectedCurrency

  // Routing section only applies to maritime offers
  const isMaritime = (opportunity?.modalityName || '').toLowerCase() === 'maritimo'

  // Load team members for the responsibles picker
  useEffect(() => {
    api.get('/freight-forwarder/team')
      .then(res => setTeamMembers(res.data.members || []))
      .catch(err => console.warn('Failed to load team members:', err))
  }, [])

  // Conditions hook - to get full condition objects for sending to backend
  const { conditions: allConditions } = useOfferConditions()

  // Load opportunity and initialize breakdown
  useEffect(() => {
    if (id) {
      if (isEditMode) {
        // We're editing an existing offer - first fetch the offer to get the quote_id
        fetchExistingOffer()
      } else {
        // We're creating a new offer - fetch the opportunity (quote) directly
        fetchOpportunity()
      }
    }
  }, [id, isEditMode])

  // On new offers, seed only platform fees. All other fees are added manually
  // (via custom-add UI or by applying a saved template).
  useEffect(() => {
    if (opportunity && breakdown.length === 0 && !isEditMode) {
      const initialBreakdown: BreakdownItem[] = []

      if (platformFees) {
        // Eximtech profit rules per modality (mirrors backend):
        //  FCL:   desc 60 (fixo) + Logistic 80 × containers (PER_CONTAINER)
        //  LCL:   desc 30 (fixo) + Logistic 1 × W/M (PER_WM)
        //  Aéreo: desc 30 (fixo) + Fuel Charge 2 × densidade (PER_KG_M3)
        // FCL values come from the configured platform fees (default 60/80).
        const isFCL = opportunity.volumeType === 'FCL'
        const isLCL = opportunity.volumeType === 'LCL'
        const isAereo = (opportunity.modalityName || '').toLowerCase() === 'aereo'
        const descValue = (isLCL || isAereo) ? 30 : platformFees.feeDesconsolidacao
        const secondName = isAereo ? 'Fuel Charge' : 'Logistic Fee'
        const secondValue = isAereo ? 2 : isLCL ? 1 : platformFees.feeLogistic
        const secondUnit = isFCL ? 'PER_CONTAINER' : isLCL ? 'PER_WM' : isAereo ? 'PER_KG_M3' : 'FORFAIT'
        initialBreakdown.push({
          id: 'platform-desconsolidacao',
          name: 'Desconsolidação',
          category: 'destination',
          value: descValue,
          currency: 'USD',
          unit: 'FORFAIT',
          isCustom: false,
          isPlatformFee: true,
        } as BreakdownItem)
        initialBreakdown.push({
          id: 'platform-logistic-fee',
          name: secondName,
          category: 'additional',
          value: secondValue,
          currency: 'USD',
          unit: secondUnit as any,
          isCustom: false,
          isPlatformFee: true,
        } as BreakdownItem)
      }

      setBreakdown(initialBreakdown)
    }
  }, [opportunity, isEditMode, inputCurrency, platformFees])

  async function fetchExistingOffer() {
    try {
      setLoading(true)
      setError(null)

      // First, fetch the offer details to get the quote_id
      const offerRes = await api.get(`/offers/${id}`)
      const offer = offerRes.data.offer

      // Draft and sent offers are editable; accepted/declined are read-only.
      // Editing a sent offer updates it in place (no re-send) and notifies the shipper.
      if (offer.status !== 'draft' && offer.status !== 'sent') {
        setReadOnly(true)
        setReadOnlyStatus(offer.status)
      }
      setEditingSent(offer.status === 'sent')

      setCurrentOfferId(offer.id)

      // Load internal change history (best-effort)
      api.get(`/freight-forwarder/offers/${offer.id}/history`)
        .then((r) => setOfferHistory(r.data.history || []))
        .catch(() => setOfferHistory([]))

      // Now fetch the opportunity (quote) details
      const oppRes = await api.get(`/freight-forwarder/opportunities/${offer.quoteId}`)
      setOpportunity(oppRes.data.opportunity)

      // Load existing offer data into the form
      if (offer.breakdown && Array.isArray(offer.breakdown)) {
        setBreakdown(offer.breakdown)
      }
      if (offer.freeTimeDays != null) setFreeTimeDays(String(offer.freeTimeDays))
      if (offer.carrierName) setCarrierName(offer.carrierName)
      if (offer.routeType) setRouteType(offer.routeType)
      if (offer.routeLegs && Array.isArray(offer.routeLegs)) setRouteLegs(offer.routeLegs)
      if (Array.isArray(offer.responsibleUserIds)) setResponsibleUserIds(offer.responsibleUserIds)
      if (Array.isArray(offer.documents)) {
        setOfferDocs(offer.documents.map((d: any) => ({
          name: d.name,
          url: d.url,
          key: d.key,
          mimeType: d.mimeType || d.mime_type || '',
          size: d.size,
        })))
      }
      if (offer.conditions && Array.isArray(offer.conditions)) {
        // Save full condition objects to avoid localStorage mismatch when saving
        const conditionsFromOffer = offer.conditions.map((c: any) => ({
          id: c.id,
          title: c.title,
          content: c.content,
        }))
        setOriginalOfferConditions(conditionsFromOffer)
        setSelectedConditionIds(conditionsFromOffer.map(c => c.id))
      }

      // Populate form fields with existing offer data
      reset({
        deliveryTime: offer.deliveryTime?.toString() || '',
        validityDate: offer.validityDate ? offer.validityDate.split('T')[0] : '',
        paymentTerms: offer.paymentTerms || '',
        paymentTermDays: offer.paymentTermDays != null ? offer.paymentTermDays.toString() : '',
        spread: offer.spread?.toString() || '',
        notes: offer.notes || '',
      })

    } catch (err: any) {
      console.error('Error fetching offer:', err)
      setError(err.response?.data?.error || 'Erro ao carregar oferta')
    } finally {
      setLoading(false)
    }
  }

  async function fetchOpportunity() {
    try {
      setLoading(true)
      setError(null)

      const res = await api.get(`/freight-forwarder/opportunities/${id}`)
      const opp = res.data.opportunity

      // Check if quote deadline has expired
      if (opp.quoteDeadline && isExpired(opp.quoteDeadline)) {
        // Redirect back to opportunities with message
        navigate('/freight-forwarder/opportunities', {
          replace: true,
          state: { message: 'O prazo para enviar propostas nesta cotação expirou.' }
        })
        return
      }

      setOpportunity(opp)

      // Store platform fees if platform agent
      if (res.data.isPlatformAgent && res.data.platformFees) {
        setPlatformFees(res.data.platformFees)
      }

      if (res.data.myOffer) {
        setMyOffer(res.data.myOffer)
        if (res.data.myOffer.status === 'draft') {
          // Draft offers can be edited
          navigate(`/freight-forwarder/offers/${res.data.myOffer.id}/edit`, { replace: true })
        } else {
          // Sent/accepted offers open in read-only mode
          navigate(`/freight-forwarder/offers/${res.data.myOffer.id}`, { replace: true })
        }
      }
    } catch (err: any) {
      console.error('Error fetching opportunity:', err)
      setError(err.response?.data?.error || 'Erro ao carregar oportunidade')
    } finally {
      setLoading(false)
    }
  }

  // Derive cargo dimensions from the opportunity for per-unit subtotal calc.
  const cargo = useMemo(
    () => computeCargoMetrics(opportunity?.volumeItems, opportunity?.volumeType),
    [opportunity?.volumeItems, opportunity?.volumeType]
  )

  // Sum subtotals per currency, no cross-currency conversion.
  const totalsByCurrency = useMemo(
    () => computeTotalsByCurrency(breakdown, inputCurrency, cargo),
    [breakdown, inputCurrency, cargo]
  )
  const totalUSD = totalsByCurrency.USD ?? 0
  const totalBRL = totalsByCurrency.BRL ?? 0
  const totalInInputCurrency = totalsByCurrency[inputCurrency] ?? 0

  // Validation: minimum $1 USD across all USD items (keeps prior behavior).
  const isOfferValid = useMemo(() => {
    return totalUSD >= 1
  }, [totalUSD])


  // Keyboard shortcuts (Ctrl+S to save draft, Ctrl+Enter to send)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (readOnly) return
      // Ctrl+S or Cmd+S - Save as draft
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (!submitting && isOfferValid) {
          handleFormSubmit((data) => handleSubmit(data, false))()
        }
      }
      // Ctrl+Enter or Cmd+Enter - Send offer
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        if (!submitting && isOfferValid) {
          handleFormSubmit((data) => handleSubmit(data, true))()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [submitting, isOfferValid, handleFormSubmit])

  // BETA: upload a rate sheet / Excel / PDF and let the AI pre-fill the offer.
  async function handleExtractOffer(file: File) {
    setExtracting(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post('/freight-forwarder/offers/extract-document', form)
      const { extracted } = res.data as { extracted: any }
      setReviewData({ extracted }) // review before applying
    } catch (err: any) {
      setError(err.response?.data?.error || 'Não foi possível ler o documento')
    } finally {
      setExtracting(false)
      if (extractInputRef.current) extractInputRef.current.value = ''
    }
  }

  // Apply the reviewed extraction into the offer form.
  function applyExtractedOffer(extracted: any) {
    const validUnits = ['FORFAIT', 'PER_KG', 'PER_CBM', 'PER_CONTAINER', 'PER_DOCUMENT', 'PER_100KG_AIR', 'PER_100KG_LCL']
    const mainDetected = normalizeCurrency(extracted.currency)
    // Fall back to the FF's selected currency only when we truly can't tell.
    const mainCurrency = mainDetected || selectedCurrency

    if (mainDetected) setSelectedCurrency(mainDetected as SupportedCurrency)

    if (Array.isArray(extracted.charges) && extracted.charges.length > 0) {
      const items: BreakdownItem[] = extracted.charges
        .filter((c: any) => c && (c.name || c.amount != null))
        .map((c: any) => {
          const cur = normalizeCurrency(c.currency) || mainCurrency
          const unit = validUnits.includes(String(c.unit).toUpperCase()) ? String(c.unit).toUpperCase() : 'FORFAIT'
          const cat = ['freight', 'origin', 'destination', 'additional'].includes(String(c.category))
            ? String(c.category)
            : 'additional'
          return {
            id: crypto.randomUUID(),
            name: String(c.name || 'Taxa'),
            category: cat,
            value: Number(c.amount) || 0,
            currency: cur,
            unit,
            isCustom: true,
          } as BreakdownItem
        })
      if (items.length > 0) setBreakdown(items)
    }

    if (extracted.transitTimeDays != null) setValue('deliveryTime', String(extracted.transitTimeDays))
    if (extracted.validityDate) setValue('validityDate', String(extracted.validityDate).slice(0, 10))
    if (extracted.paymentTerms) setValue('paymentTerms', String(extracted.paymentTerms))
    if (extracted.notes) setValue('notes', String(extracted.notes))
    if (extracted.freeTimeDays != null) setFreeTimeDays(String(extracted.freeTimeDays))
    if (extracted.carrierName) setCarrierName(String(extracted.carrierName))

    setReviewData(null)
  }

  // Record which fields the extraction missed (fire-and-forget telemetry).
  function logOfferFeedback(extracted: any, missingLabels: string[]) {
    api.post('/freight-forwarder/offers/extract-feedback', {
      missingFields: missingLabels,
      extracted,
    }).catch(() => {})
  }

  async function handleSubmit(formData: OfferFormData, sendNow: boolean) {
    // Prevent double submission using ref (more reliable than state)
    if (isSubmittingRef.current) {
      console.log('Blocked double submission')
      return
    }
    isSubmittingRef.current = true

    try {
      setSubmitting(true)
      setError(null)

      // Validate minimum value
      if (totalUSD < 1) {
        setError('O valor total da oferta deve ser no mínimo $1,00 USD')
        return
      }

      // Prepare breakdown data (only items with values > 0). No cross-currency conversion.
      const breakdownData = breakdown
        .filter(item => item.value > 0)
        .map(item => {
          const itemCurrency = item.currency || inputCurrency
          return {
            id: item.id,
            name: item.name,
            category: item.category,
            value: item.value,
            currency: itemCurrency,
            unit: item.unit || 'FORFAIT',
            isCustom: item.isCustom || false,
            isPlatformFee: item.isPlatformFee || false,
          }
        })

      if (breakdownData.length === 0) {
        setError('Adicione pelo menos uma taxa com valor maior que zero')
        return
      }

      // Map selected condition IDs to full condition objects (id, title, content)
      // When editing, use the original conditions from the offer if available
      // to avoid localStorage mismatch issues
      let selectedConditions: Array<{id: string, title: string, content: string}> | null = null

      if (selectedConditionIds.length > 0) {
        if (isEditMode && originalOfferConditions) {
          // Use original conditions from the offer (filter by selected IDs in case user deselected some)
          selectedConditions = originalOfferConditions.filter(c => selectedConditionIds.includes(c.id))

          // Also add any new conditions from localStorage that might have been selected
          const originalIds = originalOfferConditions.map(c => c.id)
          const newFromLocalStorage = allConditions
            .filter(c => selectedConditionIds.includes(c.id) && !originalIds.includes(c.id))
            .map(c => ({ id: c.id, title: c.title, content: c.content }))

          selectedConditions = [...selectedConditions, ...newFromLocalStorage]
        } else {
          // New offer - use localStorage conditions
          selectedConditions = allConditions
            .filter(c => selectedConditionIds.includes(c.id))
            .map(c => ({ id: c.id, title: c.title, content: c.content }))
        }

        // If no conditions found, set to null
        if (selectedConditions.length === 0) {
          selectedConditions = null
        }
      }

      const offerPayload = {
        currency: inputCurrency, // Default currency used for new items
        totalValue: totalUSD || totalInInputCurrency, // Back-compat: prefer USD, fallback to input currency
        totalValueBRL: totalBRL || null,
        totalValueUSD: totalUSD || null,
        totalsByCurrency,
        inputCurrency: inputCurrency,
        exchangeRateUsed: null,
        exchangeRateSource: null,
        breakdown: breakdownData,
        paymentTerms: formData.paymentTerms || null,
        paymentTermDays: formData.paymentTermDays ? parseInt(formData.paymentTermDays) : null,
        deliveryTime: formData.deliveryTime ? parseInt(formData.deliveryTime) : null,
        validityDate: formData.validityDate || null,
        spread: formData.spread ? parseFloat(formData.spread) : null,
        notes: formData.notes || null,
        conditions: selectedConditions,
        freeTimeDays: isMaritime && freeTimeDays ? parseInt(freeTimeDays) : null,
        carrierName: isMaritime && carrierName ? carrierName.trim() : null,
        routeType: isMaritime ? routeType : null,
        routeLegs: isMaritime && routeType === 'transshipment'
          ? routeLegs.filter(l => l.port.trim() && l.country.trim())
          : null,
        responsibleUserIds,
        documents: offerDocs,
      }

      let offerId: string

      if (currentOfferId) {
        // Update existing offer
        await api.patch(`/freight-forwarder/offers/${currentOfferId}`, offerPayload)
        offerId = currentOfferId
      } else {
        // Create new offer
        const createRes = await api.post('/freight-forwarder/offers', {
          ...offerPayload,
          quoteId: id,
        })
        offerId = createRes.data.offer.id
      }

      // If sendNow, also send the offer — but an already-sent offer being
      // edited stays sent (the update notifies the shipper automatically).
      if (sendNow && !editingSent) {
        await api.post(`/freight-forwarder/offers/${offerId}/send`)
      }

      navigate('/freight-forwarder/offers')
    } catch (err: any) {
      console.error('Error creating offer:', err)
      const errorMessage = err.response?.data?.error || 'Erro ao criar oferta'
      const errorDetail = err.response?.data?.details || err.message

      // Show error modal for better UX
      setErrorDetails({
        message: errorMessage,
        details: errorDetail,
        code: err.response?.status?.toString(),
        endpoint: 'POST /freight-forwarder/offers',
      })
      setErrorModalOpen(true)
      setError(errorMessage)
    } finally {
      setSubmitting(false)
      isSubmittingRef.current = false
    }
  }

  const getModalityIcon = (modalityName: string) => {
    const name = modalityName?.toLowerCase() || ''
    if (name.includes('mar')) return <Boat size={20} className="text-blue-600" />
    if (name.includes('aer') || name.includes('air'))
      return <Airplane size={20} className="text-purple-600" />
    if (name.includes('ter') || name.includes('rod'))
      return <Truck size={20} className="text-green-600" />
    return <Package size={20} className="text-gray-600" />
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <Spinner size={32} className="animate-spin text-emerald-600" />
          <span className="ml-3 text-gray-500">Carregando oportunidade...</span>
        </div>
      </AppLayout>
    )
  }

  if (error && !opportunity) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Warning size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-600 font-medium">Erro ao carregar oportunidade</p>
                <p className="text-red-500 text-sm mt-1">{error}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => navigate('/freight-forwarder/opportunities')}
            >
              Voltar para Oportunidades
            </Button>
          </div>
        </div>
      </AppLayout>
    )
  }

  if (!opportunity) return null

  return (
    <AppLayout>
      {/* Error Modal */}
      {errorDetails && (
        <ErrorModal
          isOpen={errorModalOpen}
          onClose={() => setErrorModalOpen(false)}
          error={errorDetails}
          context={{
            page: 'NewOfferPage',
            action: 'Criar Oferta',
            additionalInfo: {
              quoteId: id,
              quoteTxControl: opportunity?.txControl,
              totalValue: totalUSD,
            },
          }}
        />
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="gap-2"
              >
                <ArrowLeft size={18} />
                Voltar
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {readOnly ? 'Proposta Comercial' : isEditMode ? 'Editar Proposta' : 'Nova Proposta Comercial'}
                </h1>
                <p className="text-sm text-gray-500">{opportunity.txControl}</p>
              </div>
            </div>
            {!readOnly && (
              <div>
                <input
                  ref={extractInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx,.xls,.csv,application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleExtractOffer(e.target.files[0])}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => extractInputRef.current?.click()}
                  disabled={extracting}
                  className="gap-2 !border-emerald-600 !text-emerald-700 hover:!bg-emerald-50"
                >
                  <Sparkle size={18} weight="fill" />
                  {extracting ? 'Analisando...' : 'Importar proposta'}
                  <span className="text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">BETA</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form
          // Never auto-send on Enter / native submit — offers were going out
          // accidentally when the agent pressed Enter in a field. Sending is
          // explicit: the "Enviar Proposta" button or Ctrl+Enter shortcut.
          onSubmit={(e) => e.preventDefault()}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Read-only banner */}
            {readOnly && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-800 text-sm font-medium">
                      Proposta {readOnlyStatus === 'sent' ? 'enviada' : readOnlyStatus === 'accepted' ? 'aceita' : readOnlyStatus === 'declined' ? 'recusada' : readOnlyStatus}
                    </p>
                    <p className="text-blue-600 text-xs mt-1">
                      Esta proposta já foi enviada e não pode ser alterada. Você está visualizando em modo somente leitura.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Editing a sent offer banner */}
            {editingSent && !readOnly && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-800 text-sm font-medium">Editando proposta enviada</p>
                    <p className="text-amber-700 text-xs mt-1">
                      Você pode alterar os valores enquanto o cliente não aceitou. Ao salvar, a versão anterior fica no histórico e o cliente é notificado da atualização.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Warning size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Fee Breakdown Section — hidden on Editar Proposta (Plane #8).
                The breakdown state stays loaded from the offer, so totals are
                preserved on save even though the editor is not shown. */}
            {!isEditMode && (
            <div>
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CurrencyDollar size={20} className="text-emerald-600" />
                    Composição de Preços
                  </h2>
                  {/* Currency Selector */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-600">Moeda:</label>
                    <select
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value as SupportedCurrency)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      {SUPPORTED_CURRENCIES.map((curr) => (
                        <option key={curr.code} value={curr.code}>
                          {curr.symbol} {curr.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Selecione as taxas aplicáveis e preencha os valores em {SUPPORTED_CURRENCIES.find(c => c.code === inputCurrency)?.symbol || inputCurrency}
                </p>
              </div>

              {/* Saved Templates */}
              {!readOnly && (
                <SavedTemplatesPanel
                  modality={opportunity?.modalityName || ''}
                  currentBreakdown={breakdown}
                  currency={inputCurrency}
                  onApplyTemplate={setBreakdown}
                />
              )}

              <div className={readOnly ? 'pointer-events-none opacity-75' : ''}>
                <FeeBreakdown
                  breakdown={breakdown}
                  onChange={setBreakdown}
                  currency={inputCurrency}
                  cargo={cargo}
                />
              </div>
            </div>
            )}

            {/* Routing Section — maritime only */}
            {isMaritime && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Boat size={20} className="text-emerald-600" />
                  Rota e Roteirização
                </h2>

                <div className={readOnly ? 'pointer-events-none opacity-75' : ''}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Free Time do Container (dias)
                      </label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="Ex: 14"
                        value={freeTimeDays}
                        onChange={e => setFreeTimeDays(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Armador
                      </label>
                      <Input
                        type="text"
                        placeholder="Ex: MSC, Maersk, CMA CGM"
                        value={carrierName}
                        onChange={e => setCarrierName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Rota
                    </label>
                    <div className="flex gap-3">
                      {([['direct', 'Rota Direta'], ['transshipment', 'Com Transbordo']] as const).map(([value, label]) => (
                        <label
                          key={value}
                          className={`flex-1 flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                            routeType === value
                              ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="routeType"
                            value={value}
                            checked={routeType === value}
                            onChange={() => setRouteType(value)}
                            className="h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className="text-sm font-medium">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {routeType === 'transshipment' && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Portos de Transbordo
                        </label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setRouteLegs([...routeLegs, { port: '', country: '', eta: '', etd: '' }])}
                          className="gap-1"
                        >
                          <Plus size={14} />
                          Adicionar Porto
                        </Button>
                      </div>
                      {routeLegs.length === 0 ? (
                        <p className="text-xs text-gray-500 italic p-3 bg-gray-50 rounded">
                          Adicione cada porto intermediário entre origem e destino.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {routeLegs.map((leg, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="col-span-1 text-xs font-semibold text-gray-500 pb-2">
                                {idx + 1}º
                              </div>
                              <div className="col-span-3">
                                <label className="block text-[10px] text-gray-500 mb-0.5">Porto</label>
                                <Input
                                  type="text"
                                  placeholder="Santos"
                                  value={leg.port}
                                  onChange={e => {
                                    const next = [...routeLegs]
                                    next[idx] = { ...leg, port: e.target.value }
                                    setRouteLegs(next)
                                  }}
                                />
                              </div>
                              <div className="col-span-3">
                                <label className="block text-[10px] text-gray-500 mb-0.5">País</label>
                                <Input
                                  type="text"
                                  placeholder="Brasil"
                                  value={leg.country}
                                  onChange={e => {
                                    const next = [...routeLegs]
                                    next[idx] = { ...leg, country: e.target.value }
                                    setRouteLegs(next)
                                  }}
                                />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-[10px] text-gray-500 mb-0.5">ETA</label>
                                <Input
                                  type="date"
                                  value={leg.eta || ''}
                                  onChange={e => {
                                    const next = [...routeLegs]
                                    next[idx] = { ...leg, eta: e.target.value }
                                    setRouteLegs(next)
                                  }}
                                />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-[10px] text-gray-500 mb-0.5">ETD</label>
                                <Input
                                  type="date"
                                  value={leg.etd || ''}
                                  onChange={e => {
                                    const next = [...routeLegs]
                                    next[idx] = { ...leg, etd: e.target.value }
                                    setRouteLegs(next)
                                  }}
                                />
                              </div>
                              <div className="col-span-1">
                                <button
                                  type="button"
                                  onClick={() => setRouteLegs(routeLegs.filter((_, i) => i !== idx))}
                                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded transition-colors"
                                  title="Remover porto"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Conditions Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar size={20} className="text-emerald-600" />
                Condições Comerciais
              </h2>

              <div className="grid grid-cols-2 gap-4">
                {/* Transit Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tempo de Trânsito (dias)
                  </label>
                  <Input
                    type="number"
                    placeholder="Ex: 30"
                    disabled={readOnly}
                    {...register('deliveryTime')}
                  />
                  {errors.deliveryTime && (
                    <p className="text-xs text-red-500 mt-1">{errors.deliveryTime.message}</p>
                  )}
                </div>

                {/* Validity Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validade da Proposta
                  </label>
                  <Input type="date" disabled={readOnly} {...register('validityDate')} />
                  {errors.validityDate && (
                    <p className="text-xs text-red-500 mt-1">{errors.validityDate.message}</p>
                  )}
                </div>
              </div>

              {/* Payment Terms */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condições de Pagamento
                </label>
                <Input
                  type="text"
                  placeholder="Ex: 50% no embarque, 50% na entrega"
                  disabled={readOnly}
                  {...register('paymentTerms')}
                />
                {errors.paymentTerms && (
                  <p className="text-xs text-red-500 mt-1">{errors.paymentTerms.message}</p>
                )}
              </div>

              {/* Payment term in days — structured/sortable value (Plane #27).
                  The shipper sorts offers by "maior prazo de pagamento" with this. */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prazo de Pagamento (dias)
                </label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Ex: 30"
                  disabled={readOnly}
                  {...register('paymentTermDays')}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Número de dias para pagamento. Usado pelo embarcador para ordenar as ofertas.
                </p>
                {errors.paymentTermDays && (
                  <p className="text-xs text-red-500 mt-1">{errors.paymentTermDays.message}</p>
                )}
              </div>

              {/* Spread moved to the agent's fixed setting (Plane #25):
                  Configurações › Empresa › Cotação do Dólar. No longer per-offer. */}

              {/* Notes */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  rows={4}
                  placeholder="Informações adicionais sobre a proposta..."
                  disabled={readOnly}
                  {...register('notes')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-600"
                />
                {errors.notes && (
                  <p className="text-xs text-red-500 mt-1">{errors.notes.message}</p>
                )}
              </div>

              {/* Offer Conditions */}
              <div className={`mt-4 ${readOnly ? 'pointer-events-none opacity-75' : ''}`}>
                <OfferConditionsPanel
                  selectedConditionIds={selectedConditionIds}
                  onConditionsChange={setSelectedConditionIds}
                  externalConditions={originalOfferConditions || undefined}
                />
              </div>
            </div>

            {/* Responsibles Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <User size={20} className="text-emerald-600" />
                Responsáveis pelo Processo
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Quando a proposta for aceita, somente os selecionados receberão emails
                e enxergarão o processo. Pelo menos um é recomendado; vazio = visível à empresa toda.
              </p>

              <div className={`grid grid-cols-1 md:grid-cols-2 gap-2 ${readOnly ? 'pointer-events-none opacity-75' : ''}`}>
                {teamMembers.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Carregando equipe...</p>
                ) : (
                  teamMembers.map(member => {
                    const checked = responsibleUserIds.includes(member.id)
                    return (
                      <label
                        key={member.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          checked
                            ? 'bg-emerald-50 border-emerald-300'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setResponsibleUserIds(checked
                              ? responsibleUserIds.filter(id => id !== member.id)
                              : [...responsibleUserIds, member.id]
                            )
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {member.department ? (
                              <span className="capitalize">{member.department}</span>
                            ) : <span className="italic">sem departamento</span>}
                            {' · '}
                            <span className="text-gray-400">{member.email}</span>
                          </p>
                        </div>
                        {(member.role === 'admin' || member.role === 'master') && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {member.role}
                          </span>
                        )}
                      </label>
                    )
                  })
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm">
                {isOfferValid ? (
                  <>
                    <CheckCircle size={16} className="text-emerald-600" weight="fill" />
                    <span className="text-emerald-700 font-medium">Proposta válida</span>
                  </>
                ) : (
                  <>
                    <Warning size={16} className="text-amber-600" weight="fill" />
                    <span className="text-amber-700">Valor mínimo: $1,00 USD</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                {readOnly ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                  >
                    <ArrowLeft size={18} className="mr-2" />
                    Voltar
                  </Button>
                ) : editingSent ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      disabled={submitting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      onClick={handleFormSubmit(data => handleSubmit(data, false))}
                      disabled={submitting || !isOfferValid}
                      className="gap-2 bg-amber-600 hover:bg-amber-700"
                      title="Atualiza a proposta enviada e notifica o cliente"
                    >
                      {submitting ? (
                        <Spinner size={18} className="animate-spin" />
                      ) : (
                        <PaperPlaneTilt size={18} />
                      )}
                      Atualizar Proposta
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      disabled={submitting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleFormSubmit(data => handleSubmit(data, false))}
                      disabled={submitting || !isOfferValid}
                      className="gap-2"
                      title="Ctrl+S"
                    >
                      <FloppyDisk size={18} />
                      Salvar Rascunho
                      <kbd className="hidden sm:inline-flex ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded border border-gray-200">
                        Ctrl+S
                      </kbd>
                    </Button>
                    <Button
                      type="button"
                      onClick={handleFormSubmit(data => handleSubmit(data, true))}
                      disabled={submitting || !isOfferValid}
                      className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                      title="Ctrl+Enter"
                    >
                      {submitting ? (
                        <Spinner size={18} className="animate-spin" />
                      ) : (
                        <PaperPlaneTilt size={18} />
                      )}
                      Enviar Proposta
                      <kbd className="hidden sm:inline-flex ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-emerald-700 text-emerald-100 rounded border border-emerald-500">
                        Ctrl+Enter
                      </kbd>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6 sticky top-6">
              {/* Total Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                  Resumo da Proposta
                </h3>

                <MultiCurrencyTotal
                  totalsByCurrency={totalsByCurrency}
                  variant="full"
                  emptyLabel="Adicione taxas ao breakdown"
                />

                {/* Breakdown Summary — rate × billable = subtotal */}
                {breakdown.filter(item => item.value > 0).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Tarifas Incluídas
                    </p>
                    <div className="space-y-1.5">
                      {breakdown.filter(item => item.value > 0).map((item, index) => {
                        const unit = item.unit || 'FORFAIT'
                        const def = FEE_UNIT_MAP[unit]
                        const subtotal = computeFeeSubtotal(item.value, unit, cargo)
                        const itemCurrency = item.currency || inputCurrency
                        return (
                          <div key={`${item.id}-${index}`} className="flex items-center justify-between text-sm gap-2">
                            <span className="text-gray-600 truncate flex-1">
                              {item.name}
                              {unit !== 'FORFAIT' && (
                                <span className="text-xs text-gray-400 ml-1">
                                  ({formatCurrency(item.value, itemCurrency)}{def.shortLabel})
                                </span>
                              )}
                            </span>
                            <span className="font-medium text-gray-900 whitespace-nowrap">
                              {formatCurrency(subtotal, itemCurrency)}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Quote Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                  Detalhes da Cotação
                </h3>

                <div className="space-y-4">
                  {/* Modality */}
                  <div className="flex items-center gap-3">
                    {getModalityIcon(opportunity.modalityName)}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {opportunity.modalityName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {opportunity.modalityType === 'import' ? 'Importação' : 'Exportação'}
                      </p>
                    </div>
                  </div>

                  {/* Route */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Rota
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <span>
                        {opportunity.origin?.location?.city}, {opportunity.origin?.location?.country}
                      </span>
                      <ArrowRight size={14} className="text-gray-400" />
                      <span>
                        {opportunity.destination?.location?.city},{' '}
                        {opportunity.destination?.location?.country}
                      </span>
                    </div>
                  </div>

                  {/* Incoterm */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Incoterm
                    </p>
                    <p className="text-sm font-semibold">{opportunity.incoterm}</p>
                  </div>

                  {/* Collection address — EXW only (Plane #1). The agent prices the
                      pickup leg, so surface the collection point and date. */}
                  {opportunity.incoterm === 'EXW' && (() => {
                    const pickup = opportunity.pickupLocation?.location || opportunity.origin?.location
                    const collectDate = opportunity.pickupLocation?.collect_date || opportunity.origin?.collect_date
                    const addr = opportunity.pickupLocation?.address || opportunity.origin?.address
                    const addrLine = addr
                      ? [addr.street, addr.number, addr.neighborhood].filter(Boolean).join(', ')
                      : ''
                    return (
                      <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
                        <p className="text-xs font-medium text-sky-700 uppercase tracking-wide mb-1">
                          Endereço de Coleta (EXW)
                        </p>
                        {addrLine && <p className="text-sm text-sky-900">{addrLine}</p>}
                        <p className="text-sm text-sky-900">
                          {[pickup?.city, pickup?.state, pickup?.country].filter(Boolean).join(', ') || '—'}
                          {(addr?.zip_code || pickup?.zip_code) ? ` · CEP ${addr?.zip_code || pickup?.zip_code}` : ''}
                        </p>
                        {collectDate && (
                          <p className="text-xs text-sky-700 mt-1">
                            Data de coleta: {new Date(collectDate).toLocaleDateString('pt-BR')}
                          </p>
                        )}
                      </div>
                    )
                  })()}

                  {/* Goods */}
                  {opportunity.goodsDescription && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Mercadoria
                      </p>
                      <p className="text-sm">{opportunity.goodsDescription}</p>
                    </div>
                  )}

                  {/* Observações do embarcador */}
                  {opportunity.notes && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-1">
                        Observações do embarcador
                      </p>
                      <p className="text-sm text-amber-900 whitespace-pre-wrap">{opportunity.notes}</p>
                    </div>
                  )}

                  {/* Volume */}
                  {opportunity.volumeItems && opportunity.volumeItems.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                        Volumes ({opportunity.volumeItems.reduce((acc: number, item: any) => acc + (item.quantity || 1), 0)} {opportunity.volumeType || 'itens'})
                      </p>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-1.5 font-medium text-gray-500">Tipo</th>
                              <th className="text-left py-1.5 font-medium text-gray-500">Qtd</th>
                              <th className="text-left py-1.5 font-medium text-gray-500">Peso</th>
                              <th className="text-left py-1.5 font-medium text-gray-500">Dimensões</th>
                            </tr>
                          </thead>
                          <tbody>
                            {opportunity.volumeItems.map((item: any, idx: number) => (
                              <tr key={idx} className="border-b border-gray-100">
                                <td className="py-1.5 font-medium">{item.type}</td>
                                <td className="py-1.5">{item.quantity}</td>
                                <td className="py-1.5">{item.weight} kg</td>
                                <td className="py-1.5">
                                  {item.dimensions
                                    ? `${item.dimensions.length}x${item.dimensions.width}x${item.dimensions.height} ${item.dimensions.unit}`
                                    : item.volume_m3 ? `${item.volume_m3} m³` : '-'
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-x-6 gap-y-1 text-xs">
                        <span>
                          <span className="text-gray-500">Peso total: </span>
                          <span className="font-semibold text-gray-900">
                            {cargo.totalWeightKg.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg
                          </span>
                        </span>
                        <span>
                          <span className="text-gray-500">Cubagem total: </span>
                          <span className="font-semibold text-gray-900">
                            {cargo.totalCubicMeters.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} m³
                          </span>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Documents attached by the shipper */}
                  {opportunity.documents && opportunity.documents.length > 0 && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                        Documentos do embarcador
                      </p>
                      <DocumentAttachments
                        readOnly
                        documents={opportunity.documents.map((d: any) => ({
                          name: d.name,
                          url: d.url,
                          key: d.key,
                          mimeType: d.mimeType || d.type || '',
                          size: d.size,
                        }))}
                      />
                    </div>
                  )}

                  {/* Shipper */}
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Embarcador
                    </p>
                    <p className="text-sm font-medium">{opportunity.shipperName}</p>
                  </div>

                  {/* Competition */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Concorrência
                    </p>
                    <p className="text-sm">
                      <span className="font-semibold">{opportunity.totalOffersCount}</span>{' '}
                      propostas enviadas
                    </p>
                  </div>
                </div>
              </div>

              {/* Offer documents — attached by the agent */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-1">
                  Documentos da oferta
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Anexe tabelas de frete, cotações de armador, etc. O embarcador poderá visualizá-los.
                </p>
                <DocumentAttachments
                  documents={offerDocs}
                  onChange={setOfferDocs}
                  uploader={uploadOfferDocument}
                  readOnly={readOnly}
                />
              </div>

              {/* Internal change history */}
              {offerHistory.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-1">
                    Histórico de alterações
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Registro interno das versões anteriores desta proposta.
                  </p>
                  <div className="space-y-2">
                    {offerHistory.map((h) => (
                      <div key={h.id} className="text-xs border border-gray-100 rounded-lg p-2.5">
                        <div className="flex items-center justify-between text-gray-500">
                          <span>{h.changedByName || 'Agente'}</span>
                          <span>{formatDateBR(h.createdAt)}</span>
                        </div>
                        <div className="mt-1 text-gray-700">
                          Valor: <span className="font-medium">{h.snapshot?.totalValue != null ? formatCurrency(Number(h.snapshot.totalValue), h.snapshot.currency || 'USD') : '—'}</span>
                          {h.snapshot?.deliveryTime != null && <span> · {h.snapshot.deliveryTime} dias</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </main>

      {/* BETA: review what the AI captured before pre-filling the offer */}
      {reviewData && (() => {
        const ex = reviewData.extracted || {}
        const charges = Array.isArray(ex.charges) ? ex.charges : []
        const rows: { label: string; value: string | null }[] = [
          { label: 'Moeda', value: ex.currency || null },
          { label: 'Armador / Cia', value: ex.carrierName || null },
          { label: 'Tempo de trânsito (dias)', value: ex.transitTimeDays != null ? String(ex.transitTimeDays) : null },
          { label: 'Validade', value: ex.validityDate ? String(ex.validityDate).slice(0, 10) : null },
          { label: 'Free time (dias)', value: ex.freeTimeDays != null ? String(ex.freeTimeDays) : null },
          { label: 'Condições de pagamento', value: ex.paymentTerms || null },
          { label: 'Observações', value: ex.notes || null },
        ]
        const captured = rows.filter(r => r.value)
        const missing = rows.filter(r => !r.value)
        if (charges.length === 0) missing.push({ label: 'Taxas / composição', value: null })
        return (
          <Dialog isOpen={true} onClose={() => setReviewData(null)} maxWidth="lg">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <Sparkle size={20} weight="fill" className="text-emerald-600" />
                <h2 className="text-lg font-bold text-gray-900">Revise a proposta importada</h2>
                <span className="text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">BETA</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {charges.length} taxa(s) e {captured.length} de {rows.length} campos capturados. Confira antes de aplicar.
              </p>
            </DialogHeader>
            <DialogContent className="max-h-[60vh] overflow-y-auto">
              <div className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <div key={r.label} className="flex items-start gap-3 py-2.5">
                    {r.value
                      ? <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
                      : <WarningCircle size={18} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-gray-500">{r.label}</div>
                      <div className={`text-sm ${r.value ? 'text-gray-900' : 'text-amber-600 italic'}`}>
                        {r.value || 'não capturado — preencha manualmente'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {charges.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Taxas capturadas</div>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {charges.map((c: any, i: number) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-gray-800 truncate">{c.name || 'Taxa'}</span>
                        <span className="font-medium text-gray-900 shrink-0 ml-3">
                          {(normalizeCurrency(c.currency) || normalizeCurrency(ex.currency) || '')} {Number(c.amount) || 0}
                          {c.unit && c.unit !== 'FORFAIT' ? <span className="text-gray-400 text-xs"> /{c.unit}</span> : null}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {missing.length > 0 && (
                <p className="mt-3 text-xs text-gray-400">
                  {missing.length} item(ns) não encontrado(s). Registramos isso (anônimo) para melhorar a extração.
                </p>
              )}
            </DialogContent>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReviewData(null)}>
                Cancelar
              </Button>
              <Button
                type="button"
                className="!bg-emerald-600 !text-white hover:!bg-emerald-700"
                onClick={() => {
                  logOfferFeedback(ex, missing.map(m => m.label))
                  applyExtractedOffer(reviewData.extracted)
                }}
              >
                Confirmar e preencher
              </Button>
            </DialogFooter>
          </Dialog>
        )
      })()}
    </AppLayout>
  )
}
